import { Logger } from '@/Logger';

import { ApiError } from './errors/ApiError';
import type { AiSuggestionV2Request } from './TeoApi';
import type { TeoCore } from './TeoCore';
import { getAISuggestionPrompt } from './text/AIHandler/aiSuggestionPresets';
import { TeoText } from './text/TeoText';
import { TeoEventTarget } from './utility/TeoEventTarget';

const _LOG = Logger('TextCreator');

type TeoCreaterEvents = {
  textCreatedFromAiSuggestion: (details: TeoText) => void;
};

export class TextCreator extends TeoEventTarget<TeoCreaterEvents> {
  private core: TeoCore;

  constructor(core: TeoCore) {
    super();
    this.core = core;
  }

  async createPlainLanguage(sourceText: string): Promise<TeoText> {
    const suggestion = await this.getPlainLanguageSuggestion(sourceText);

    const newText = this.core.createTemporaryText(suggestion);
    if (suggestion.trim().length > 0) {
      this.triggerEvent('textCreatedFromAiSuggestion', newText);
    }
    return newText;
  }

  private async getPlainLanguageSuggestion(sourceText: string): Promise<string> {
    if (
      !this.core.activeText.state.allPromptOptionsSelected ||
      !this.core.activeText.state.currentTemplateId
    ) {
      throw new Error('Not all prompt options selected.');
    }

    // const prompt = this.getPrompts(promptType).find((p) => p.id === promptId);
    const prompt = getAISuggestionPrompt('translate_plain_language');
    if (!prompt) {
      console.error(`No prompt found for type "translate_plain_language".`);
      return '';
    }

    const aiRequest: AiSuggestionV2Request = {
      context: {
        text: sourceText,
        textAfter: '',
        textBefore: '',
        textFocus: '',
      },
      evaluation_profile_id: this.core.dynamicConfig.options.evaluationProfileId,
      selected_options: this.core.activeText.state.selectedPromptOptions as Record<string, string>,
      template_id: this.core.activeText.state.currentTemplateId,
      preset: prompt.type,
      use_whitelist: this.core.activeText.whiteListActive,
    };

    const suggestion = await this.core.api.getAiSuggestionV2(aiRequest);
    if (ApiError.matches(suggestion) || !suggestion.suggestions?.[0]) {
      console.error('Failed to get AI suggestion:', suggestion);
      return '';
    }
    return suggestion.suggestions[0].text;
  }
}
